import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { setSelectedPhotos, resizeSelectedPhotos } from '../store/photoSlice';

export const usePhotoSelection = () => {
  const [viewMode, setViewMode] = useState<'original' | 'ai'>('original');
  const { selectedPhotos, aiProcessedPhotos, selectedFrameIndex } = useSelector(
    (state: RootState) => state.photo
  );
  const selectedThemeId = useSelector((state: RootState) => state.theme.selectedThemeId);
  const dispatch = useDispatch();

  const block = useSelector((state: RootState) => state.block.block);
  const theme =
    selectedThemeId && block?.themes
      ? block.themes.find((t) => t.id === selectedThemeId) || block.themes[0]
      : block?.themes?.[0];
  const frame = theme?.frames[selectedFrameIndex];

  const PHOTOS_PER_PAGE = 4;
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(aiProcessedPhotos.length / PHOTOS_PER_PAGE);

  useEffect(() => {
    if (frame && frame.displayPhotoPositions) {
      const requiredPhotoCount = frame.displayPhotoPositions.length;
      if (selectedPhotos.length !== requiredPhotoCount) {
        dispatch(resizeSelectedPhotos(requiredPhotoCount));
      }
    }
  }, [frame, selectedPhotos.length, dispatch]);

  const toggleMode = () => {
    setViewMode((prev) => (prev === 'original' ? 'ai' : 'original'));
  };

  const choosePhotos = (index: number, type: 'skinImprovement' | 'characterize') => {
    const newSelectedPhotos = [...selectedPhotos];
    const existingIndex = newSelectedPhotos.findIndex(
      (photo) => photo && photo.photoIndex === index && photo.type === type
    );

    if (existingIndex !== -1) {
      newSelectedPhotos[existingIndex] = null;
    } else {
      const emptyIndex = newSelectedPhotos.findIndex((photo) => !photo);
      if (emptyIndex !== -1) {
        newSelectedPhotos[emptyIndex] = { photoIndex: index, type };
      }
    }

    dispatch(setSelectedPhotos(newSelectedPhotos));
  };

  const getCurrentPagePhotos = () => {
    const startIndex = currentPage * PHOTOS_PER_PAGE;
    const pagePhotos = [];

    for (let i = 0; i < PHOTOS_PER_PAGE; i++) {
      const photoIndex = startIndex + i;
      const originalPhoto = aiProcessedPhotos[photoIndex];
      const photo = originalPhoto
        ? {
            skinImprovement: originalPhoto.skinImprovement || '',
            characterize: originalPhoto.characterize || '',
            isEmptySlot: false,
          }
        : { skinImprovement: '', characterize: '', isEmptySlot: true };
      pagePhotos.push(photo);
    }

    return pagePhotos;
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return {
    viewMode,
    toggleMode,
    choosePhotos,
    selectedPhotos,
    aiProcessedPhotos,
    selectedFrameIndex,
    currentPage,
    totalPages,
    getCurrentPagePhotos,
    goToNextPage,
    goToPrevPage,
  };
};
